<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { scrollLock } from "@magnit/core/src/helpers/scrollLock";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import { useAnalyticManager } from "@magnit/analytic-events";
import MenuSquareIcon from "@magnit/icons/src/assets/icons/menu-square-24.svg";
import CloseIcon from "@magnit/icons/src/assets/icons/close-24.svg";

import type { IHeaderProject } from "../header.types";

const props = defineProps<{
  project?: IHeaderProject;
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

function openToggle(v: boolean) {
  emit("update:open", v);
}

const { send } = useAnalyticManager();
const rootElement = ref<HTMLDivElement>();

const buttonTheme = computed(() => {
  switch (props.project) {
    case "cosmetic":
      return "border";
    default:
      return "primary";
  }
});

const onButtonClick = () => {
  send("Toolbar:Catalog:Button:Click");
  openToggle(!props.open);
};

onClickOutside(rootElement, () => {
  if (props.open) {
    openToggle(false);
  }
});

watch(
  () => props.open,
  (next) => {
    next ? scrollLock.enable() : scrollLock.disable();
    if (next) {
      send("CatalogMenu:View");
    }
  },
);

onMounted(() => {
  send("Toolbar:Catalog:Button:View");
});
</script>

<template>
  <div ref="rootElement" class="pl-header-catalog">
    <div class="pl-header-catalog-button">
      <VHidden :hide="{ xs: true, s: false, l: true, xl: false }">
        <VButton
          class="pl-header-catalog-button_white"
          :theme="buttonTheme"
          icon-position="left"
          data-test-id="main-button-wide"
          @click="onButtonClick"
        >
          <template #icon>
            <MenuSquareIcon v-if="!open" />
            <CloseIcon v-else />
          </template>
          Каталог
        </VButton>
      </VHidden>

      <VHidden :hide="{ xs: false, s: true, l: false, xl: true }">
        <VButton
          class="pl-header-catalog-button_white"
          :theme="buttonTheme"
          icon-position="left"
          data-test-id="main-button-short"
          @click="onButtonClick"
        >
          <template #icon>
            <MenuSquareIcon v-if="!open" />
            <CloseIcon v-else />
          </template>
        </VButton>
      </VHidden>
    </div>
    <Transition name="slide-fade">
      <div
        v-show="open"
        class="pl-header-catalog-menu"
        @click.self="openToggle(false)"
      >
        <div class="pl-header-catalog-menu__content">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="postcss">
.pl-header-catalog-menu {
  position: fixed;
  left: 0;
  top: var(--header-project-nav-height);
  bottom: 0;
  width: 100%;
  z-index: var(--pl-z-index-overlay);
  display: flex;

  &__content {
    background-color: var(--pl-surface-primary-default);
    border-top: 1px solid var(--pl-divider-primary);
    transition: all 0.3s ease-out;
  }
}

@media (--pl-viewport-from-s) {
  .pl-header-catalog-menu__content {
    --pl-header-catalog-menu-content: var(--pl-wrapper-indent);

    padding-left: var(--pl-header-catalog-menu-content);
  }
}

@media (--pl-viewport-from-ml) {
  .pl-header-catalog-menu {
    &__content {
      box-sizing: content-box;
      padding-left: max(
        calc(
          (100% - var(--pl-wrapper-end)) / 2 +
            var(--pl-header-catalog-menu-content)
        ),
        var(--pl-header-catalog-menu-content)
      );
    }
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;

  .pl-header-catalog-menu__content {
    transform: translateX(-100px);
  }
}
</style>
